/**
 * Menu service — reads from Supabase (Saheeh's real backend).
 *
 * Keeps the original `FirebaseMenuService` class name + public API so the
 * dashboard and other consumers don't need to change. The Firestore client
 * is no longer touched here: Saheeh's menu lives in the `categories` and
 * `menu_items` tables in Supabase, scoped to the signed-in user's tenant
 * via RLS.
 */

import { supabase } from './supabaseClient';

export interface MenuItem {
  id: string;
  name: string;
  category: string;
  base_price: number;
  price_with_tax: number;
  description: string;
  name_de: string;
  description_de: string;
  tax_rate: number;
  is_available: boolean;
  is_popular: boolean;
  sold_out?: boolean;
  display_order?: number;
  display_order_by_brand?: { saheeh?: number; pizza_brand?: number } | null;
  image_url?: string;
  modifier_group_ids?: string[];
  created_at: string;
  updated_at: string;
}

export interface Category {
  id?: string;
  name: string;
  icon?: string;
  image_url?: string;
  is_active?: boolean;
  display_order?: number;
  display_order_by_brand?: { saheeh?: number; pizza_brand?: number } | null;
  item_count?: number;
  items?: string[];
}

type SupabaseMenuRow = {
  id: string;
  category_id: string | null;
  name: string;
  name_local: string | null;
  description: string | null;
  description_local: string | null;
  image_url: string | null;
  base_price: number;
  vat_rate: number;
  is_available: boolean;
  display_order: number | null;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
  categories?: { name: string } | null;
};

type SupabaseCategoryRow = {
  id: string;
  name: string;
  image_url: string | null;
  is_active: boolean;
  display_order: number;
};

class FirebaseMenuService {
  private static instance: FirebaseMenuService;
  private menuItems: MenuItem[] = [];
  private categories: Category[] = [];
  private isLoading = false;
  private realtimeChannel: ReturnType<typeof supabase.channel> | null = null;
  private menuItemsListeners: Set<(items: MenuItem[]) => void> = new Set();
  private initialFetchPromise: Promise<void> | null = null;

  private constructor() {
    console.log('🟢 [MENU] Supabase-backed menu service initialised');
    this.setupRealtime();
    this.initialFetchPromise = this.fetchAll().catch((err) => {
      console.error('❌ [MENU] Initial fetch failed:', err);
    });
  }

  public static getInstance(): FirebaseMenuService {
    if (!FirebaseMenuService.instance) {
      FirebaseMenuService.instance = new FirebaseMenuService();
    }
    return FirebaseMenuService.instance;
  }

  private mapItem(row: SupabaseMenuRow, categoryName: string | undefined): MenuItem {
    const taxRatePct = Math.round((row.vat_rate ?? 0) * 100);
    const price = Number(row.base_price) || 0;
    return {
      id: row.id,
      name: row.name,
      category: categoryName ?? '',
      base_price: price,
      price_with_tax: price,
      description: row.description ?? '',
      name_de: row.name_local ?? row.name,
      description_de: row.description_local ?? row.description ?? '',
      tax_rate: taxRatePct,
      is_available: row.is_available,
      is_popular: (row.tags ?? []).includes('popular'),
      sold_out: !row.is_available,
      display_order: row.display_order ?? 0,
      image_url: row.image_url ?? '',
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  }

  private mapCategory(row: SupabaseCategoryRow): Category {
    return {
      id: row.id,
      name: row.name,
      icon: '',
      image_url: row.image_url ?? '',
      is_active: row.is_active,
      display_order: row.display_order,
      item_count: 0,
      items: [],
    };
  }

  private async fetchAll(): Promise<void> {
    this.isLoading = true;
    try {
      const [{ data: catRows, error: catErr }, { data: itemRows, error: itemErr }] = await Promise.all([
        supabase
          .from('categories')
          .select('id, name, image_url, is_active, display_order')
          .eq('is_active', true)
          .order('display_order', { ascending: true }),
        supabase
          .from('menu_items')
          .select('id, category_id, name, name_local, description, description_local, image_url, base_price, vat_rate, is_available, display_order, tags, created_at, updated_at, categories(name)')
          .order('name', { ascending: true }),
      ]);

      if (catErr) throw catErr;
      if (itemErr) throw itemErr;

      const catById = new Map<string, string>();
      (catRows ?? []).forEach((c: SupabaseCategoryRow) => catById.set(c.id, c.name));

      this.categories = (catRows ?? []).map((c) => this.mapCategory(c as SupabaseCategoryRow));

      this.menuItems = (itemRows ?? []).map((row) => {
        const r = row as unknown as SupabaseMenuRow;
        const catName = r.categories?.name ?? (r.category_id ? catById.get(r.category_id) : undefined);
        return this.mapItem(r, catName);
      });

      console.log(`✅ [MENU] Loaded ${this.menuItems.length} items / ${this.categories.length} categories from Supabase`);

      this.menuItemsListeners.forEach((listener) => {
        try {
          listener([...this.menuItems]);
        } catch (error) {
          console.error('❌ [MENU] Listener callback error:', error);
        }
      });
    } finally {
      this.isLoading = false;
    }
  }

  private setupRealtime(): void {
    try {
      this.realtimeChannel = supabase
        .channel('menu_items_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'menu_items' }, () => {
          console.log('🔄 [MENU] menu_items change received — refetching');
          void this.fetchAll();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'categories' }, () => {
          console.log('🔄 [MENU] categories change received — refetching');
          void this.fetchAll();
        })
        .subscribe();
    } catch (error) {
      console.warn('⚠️ [MENU] Realtime subscription not available:', error);
    }
  }

  public subscribeToMenuItems(callback: (items: MenuItem[]) => void): () => void {
    this.menuItemsListeners.add(callback);
    callback([...this.menuItems]);
    return () => {
      this.menuItemsListeners.delete(callback);
    };
  }

  public async getMenuItems(forceRefresh: boolean = false): Promise<MenuItem[]> {
    if (forceRefresh || this.menuItems.length === 0) {
      if (this.initialFetchPromise && !forceRefresh) {
        await this.initialFetchPromise;
      } else {
        await this.fetchAll();
      }
    }
    return [...this.menuItems];
  }

  public async getMenuItemsByCategory(category: string): Promise<MenuItem[]> {
    const allItems = await this.getMenuItems();
    return allItems.filter((item) => item.category === category);
  }

  public async searchMenuItems(searchQuery: string): Promise<MenuItem[]> {
    const allItems = await this.getMenuItems();
    const q = searchQuery.toLowerCase();
    return allItems.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.name_de.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.description_de.toLowerCase().includes(q),
    );
  }

  public async getCategories(): Promise<Category[]> {
    if (this.categories.length === 0) {
      if (this.initialFetchPromise) await this.initialFetchPromise;
      else await this.fetchAll();
    }
    return [...this.categories];
  }

  public async getMenuItemById(id: string): Promise<MenuItem | null> {
    const cached = this.menuItems.find((item) => item.id === id);
    if (cached) return cached;
    const { data, error } = await supabase
      .from('menu_items')
      .select('id, category_id, name, name_local, description, description_local, image_url, base_price, vat_rate, is_available, display_order, tags, created_at, updated_at, categories(name)')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    const r = data as unknown as SupabaseMenuRow;
    return this.mapItem(r, r.categories?.name);
  }

  public async getPopularMenuItems(): Promise<MenuItem[]> {
    const allItems = await this.getMenuItems();
    return allItems.filter((item) => item.is_popular);
  }

  public async getAvailableMenuItems(): Promise<MenuItem[]> {
    const allItems = await this.getMenuItems();
    return allItems.filter((item) => item.is_available);
  }

  public async refreshData(): Promise<void> {
    console.log('🔄 [MENU] Manual refresh requested');
    await this.fetchAll();
  }

  public cleanup(): void {
    if (this.realtimeChannel) {
      void supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }
    this.menuItemsListeners.clear();
  }

  public isLoadingData(): boolean {
    return this.isLoading;
  }

  public getDataStats(): { totalItems: number; totalCategories: number; lastUpdated: string } {
    return {
      totalItems: this.menuItems.length,
      totalCategories: this.categories.length,
      lastUpdated: new Date().toISOString(),
    };
  }
}

export const firebaseMenuService = FirebaseMenuService.getInstance();

export default firebaseMenuService;
